import { Injectable, inject } from '@angular/core';
import {
  FirestoreInstances,
  Query,
  Timestamp,
  collection,
  collectionData,
  doc,
  docData,
  orderBy,
  query,
  where,
} from '@angular/fire/firestore';
import { EnvironmentService } from '@galaxy/core';
import { Observable } from 'rxjs';
import { ConversationFilters } from './conversation-filters';
import { FIRESTORE_ZERO_TIMESTAMP } from './conversation.constants';
import { FirestoreEvent, ConversationMessage, FirestoreConversation } from './interface/conversation.interface';

const validOriginAppLocations = ['business_app', 'partner_center', 'sales_center', 'vendor_center', 'task_manager'];

const getFirestore = (name: string) => {
  const fsi = inject(FirestoreInstances);
  return fsi.find((fs) => fs.app.name === name)!;
};

@Injectable()
export class FirestoreService {
  private firestore = getFirestore('inbox');
  private firestoreRootPath = `conversation/env/${this.environmentService.getEnvironmentString()}`;

  constructor(private readonly environmentService: EnvironmentService) {}

  private getFirestoreMessagesRootPath(conversationDocId: string): string {
    return `${this.firestoreRootPath}/${conversationDocId}/messages`;
  }

  private getFirestoreEventsRootPath(conversationDocId: string): string {
    return `${this.firestoreRootPath}/${conversationDocId}/events`;
  }

  conversation$(conversationDocId: string) {
    return docData<FirestoreConversation>(doc(this.firestore, `${this.firestoreRootPath}/${conversationDocId}`));
  }

  setupBaseConversationsQuery(
    organizationId: string,
    filters?: ConversationFilters | null,
    groupId?: string | null,
    skipIsOpenInQuery = false,
    skipIsAnonymousInQuery = false,
  ): Query<FirestoreConversation> {
    let q = query(collection(this.firestore, this.firestoreRootPath), where('deleted', '==', FIRESTORE_ZERO_TIMESTAMP));

    if (groupId) {
      q = query(q, where('subjectParticipantIds', 'array-contains', groupId));
    } else {
      q = query(q, where('subjectParticipantKeyIds', 'array-contains', organizationId));
    }

    // Anonymous should show all anonymous conversations regardless of if they've been closed
    if (!skipIsOpenInQuery && !filters?.isAnonymous) {
      q = query(q, where('isOpen', '==', filters?.isOpen ?? true));
    }

    if (!skipIsAnonymousInQuery) {
      q = query(q, where('isAnonymous', '==', filters?.isAnonymous ?? false));
    }

    return query(
      q,
      where('originApp', 'in', validOriginAppLocations),
      where('latestRelevantActivityTime', '!=', FIRESTORE_ZERO_TIMESTAMP),
      where('originAppExternalID', '==', ''),
      orderBy('latestRelevantActivityTime', 'desc'),
    );
  }

  loadOrgRelatedConversationsQuery(organizationId: string): Query<FirestoreConversation> {
    const q = this.setupBaseConversationsQuery(organizationId);
    return query(q, where('channel', '==', 'platform'));
  }

  loadUserRelatedConversationsQuery(userId: string): Query<FirestoreConversation> {
    return query(
      collection(this.firestore, this.firestoreRootPath),
      where('deleted', '==', FIRESTORE_ZERO_TIMESTAMP),
      where('subjectParticipantKeyIds', 'array-contains', userId),
      where('originApp', 'in', validOriginAppLocations),
      where('latestRelevantActivityTime', '!=', FIRESTORE_ZERO_TIMESTAMP),
      where('originAppExternalID', '==', ''),
      orderBy('latestRelevantActivityTime', 'desc'),
    );
  }

  messages$(docId: string) {
    return collectionData(
      query(
        collection(this.firestore, this.getFirestoreMessagesRootPath(docId)),
        where('deleted', '==', FIRESTORE_ZERO_TIMESTAMP),
        orderBy('created', 'desc'),
      ),
      { idField: 'id' },
    ) as Observable<ConversationMessage[]>;
  }

  webchatMessages$(docId: string) {
    return collectionData(
      query(
        collection(this.firestore, this.getFirestoreMessagesRootPath(docId)),
        where('deleted', '==', FIRESTORE_ZERO_TIMESTAMP),
        where('channel', '==', 'WebChat'),
        where('type', 'in', ['message', 'booking_availability']),
        orderBy('created', 'desc'),
      ),
      { idField: 'id' },
    ) as Observable<ConversationMessage[]>;
  }

  events$(docId: string) {
    return collectionData(
      query(collection(this.firestore, this.getFirestoreEventsRootPath(docId)), orderBy('happenedAt', 'desc')),
    ) as Observable<FirestoreEvent[]>;
  }

  getViewsConversationsQuery(
    participantId: string,
    viewId: string,
    latestRelevantActivityTime?: Timestamp,
  ): Query<FirestoreConversation> {
    let q = query(
      collection(this.firestore, this.firestoreRootPath),
      where('deleted', '==', FIRESTORE_ZERO_TIMESTAMP),
      where('conversationViewIds', 'array-contains', participantId + ':' + viewId),
      where('originApp', 'in', validOriginAppLocations),
    );
    if (latestRelevantActivityTime) {
      q = query(q, where('latestRelevantActivityTime', '<=', latestRelevantActivityTime));
    }
    return query(
      q,
      where('latestRelevantActivityTime', '!=', FIRESTORE_ZERO_TIMESTAMP),
      where('originAppExternalID', '==', ''),
      orderBy('latestRelevantActivityTime', 'desc'),
    );
  }

  getUnreadConversationsByView(participantId: string, viewId: string): Query<FirestoreConversation> {
    return query(
      collection(this.firestore, this.firestoreRootPath),
      where('deleted', '==', FIRESTORE_ZERO_TIMESTAMP),
      where('unreadConversationViewIds', 'array-contains', participantId + ':' + viewId),
      where('originApp', 'in', validOriginAppLocations),
      where('latestRelevantActivityTime', '!=', FIRESTORE_ZERO_TIMESTAMP),
      where('originAppExternalID', '==', ''),
    );
  }
}
